import { ItemType, LootBoxInfo, TILE_SIZE } from "./common";
import { BLUEPRINT_BG, BLUEPRINT_LINE } from "../ui/colors";

// Chests are locked for the first 3 minutes of a match (mirrors server/src/models/loot-box.ts)
export const CHEST_LOCK_DURATION_MS = 3 * 60 * 1000;

export class LootBox {
  info: LootBoxInfo;
  outlineGraphics?: Phaser.GameObjects.Graphics;
  materialOutlineGraphics?: Phaser.GameObjects.Graphics;
  blueprintGraphics?: Phaser.GameObjects.Graphics;
  lockIndicator?: Phaser.GameObjects.Container;
  lockTimerText?: Phaser.GameObjects.Text;
  sprite?: Phaser.GameObjects.Image;
  fixed: boolean = false; // Indicates if this loot box is fixed in place (e.g., a chest) or can be removed (e.g., a loot bag)
  isChest: boolean = false; // True for map "Chest" containers, which are time-locked at match start
  locked: boolean = false; // Current lock state, refreshed each second by MainScene while isChest is true

  constructor(info: LootBoxInfo, sprite?: Phaser.GameObjects.Image) {
    this.info = info;
    this.sprite = sprite;
  }

  /** Recomputes `locked` from the match start time and returns it. No-op (always unlocked) for non-chests. */
  refreshLockState(matchStartTime?: number): boolean {
    if (!this.isChest || !matchStartTime) {
      this.locked = false;
      return this.locked;
    }
    this.locked = Date.now() - matchStartTime < CHEST_LOCK_DURATION_MS;
    return this.locked;
  }

  /** Shows/updates the lock icon + countdown above a locked chest. */
  showLockTimer(scene: Phaser.Scene, remainingSeconds: number) {
    const mins = Math.floor(remainingSeconds / 60);
    const secs = remainingSeconds % 60;
    const label = `${mins}:${secs.toString().padStart(2, "0")}`;

    if (!this.lockIndicator) {
      const px = this.info.x * TILE_SIZE + TILE_SIZE / 2;
      const py = this.info.y * TILE_SIZE - 8;
      const iconSize = 12;
      const gap = 4;

      const icon = scene.add.image(0, 0, "lock-icon").setDisplaySize(iconSize, iconSize);
      this.lockTimerText = scene.add
        .text(0, 0, label, {
          fontSize: "12px",
          color: "#ffffff",
          fontFamily: "Anton",
          stroke: "#000000",
          strokeThickness: 3,
        })
        .setOrigin(0, 0.5);

      // Center the icon+text pair as a group
      const totalWidth = iconSize + gap + this.lockTimerText.width;
      icon.setPosition(-totalWidth / 2 + iconSize / 2, 0);
      this.lockTimerText.setPosition(icon.x + iconSize / 2 + gap, 0);

      this.lockIndicator = scene.add
        .container(px, py, [icon, this.lockTimerText])
        .setDepth((this.sprite?.depth ?? 1000) + 1);

      scene.cameras.cameras.forEach((camera: Phaser.Cameras.Scene2D.Camera) => {
        if (camera !== scene.cameras.main) {
          camera.ignore(this.lockIndicator!);
        }
      });
    } else {
      this.lockTimerText?.setText(label);
    }
  }

  hideLockTimer() {
    this.lockIndicator?.destroy();
    this.lockIndicator = undefined;
    this.lockTimerText = undefined;
  }

  // Returns true if the player is adjacent (including diagonals)
  isPlayerAdjacent(playerTileX: number, playerTileY: number): boolean {
    const dx = Math.abs(this.info.x - playerTileX);
    const dy = Math.abs(this.info.y - playerTileY);
    return dx <= 1 && dy <= 1 && !(dx === 0 && dy === 0);
  }

  // Returns true if the player is on the same tile as the loot box
  isPlayerOnSameTile(playerTileX: number, playerTileY: number): boolean {
    return this.info.x === playerTileX && this.info.y === playerTileY;
  }

  // Draws a white outline around the loot box sprite if player is adjacent
  renderOutline(scene: Phaser.Scene) {
    const px = this.info.x * TILE_SIZE;
    const py = this.info.y * TILE_SIZE;
    if (!this.outlineGraphics) {
      this.outlineGraphics = scene.add.graphics();
      this.outlineGraphics.setDepth(1); // Same depth as tile-effects layer

      // Ensure outline is only visible in the main camera, not the UI camera
      const cameras = scene.cameras.cameras;
      cameras.forEach((camera: Phaser.Cameras.Scene2D.Camera) => {
        if (camera !== scene.cameras.main) {
          camera.ignore(this.outlineGraphics!);
        }
      });
    }
    this.outlineGraphics.clear();
    this.outlineGraphics.lineStyle(3, 0xffffff, 1);
    this.outlineGraphics.strokeRect(px, py, TILE_SIZE, TILE_SIZE);
    this.outlineGraphics.visible = true;
  }

  hideOutline() {
    if (this.outlineGraphics) {
      this.outlineGraphics.visible = false;
    }
  }

  /** Draws a permanent colored outline for single-item Material loot bags based on price. */
  createMaterialOutline(scene: Phaser.Scene) {
    this.materialOutlineGraphics?.destroy();
    this.materialOutlineGraphics = undefined;

    if (this.info.isAirDrop || this.info.items.length !== 1) return;
    const item = this.info.items[0];
    if (item.definition.type !== ItemType.Material) return;

    const price = item.definition.price;
    let color: number;
    if (price < 11) {
      color = 0x888888; // gray
    } else if (price < 100) {
      color = 0x4488ff; // blue
    } else {
      color = 0xffd700; // yellow
    }

    const gfx = scene.add.graphics();
    gfx.setDepth((this.sprite?.depth ?? 1000) - 1);
    scene.cameras.cameras.forEach((camera: Phaser.Cameras.Scene2D.Camera) => {
      if (camera !== scene.cameras.main) {
        camera.ignore(gfx);
      }
    });

    const px = this.info.x * TILE_SIZE + 2;
    const py = this.info.y * TILE_SIZE + 2;
    gfx.lineStyle(2, color, 1);
    gfx.strokeRect(px, py, TILE_SIZE - 4, TILE_SIZE - 4);
    this.materialOutlineGraphics = gfx;
  }

  /** Draws a filled blueprint background behind the sprite for single-item Recipe loot bags. */
  createBlueprintBackground(scene: Phaser.Scene) {
    this.blueprintGraphics?.destroy();
    this.blueprintGraphics = undefined;

    if (this.info.isAirDrop || this.info.items.length !== 1) return;
    const item = this.info.items[0];
    if (item.definition.type !== ItemType.Recipe) return;

    const gfx = scene.add.graphics();
    gfx.setDepth((this.sprite?.depth ?? 1000) - 1);
    scene.cameras.cameras.forEach((camera: Phaser.Cameras.Scene2D.Camera) => {
      if (camera !== scene.cameras.main) {
        camera.ignore(gfx);
      }
    });

    const px = this.info.x * TILE_SIZE + 2;
    const py = this.info.y * TILE_SIZE + 2;
    const size = TILE_SIZE - 4;
    gfx.fillStyle(BLUEPRINT_BG, 1);
    gfx.fillRect(px, py, size, size);
    gfx.lineStyle(2, BLUEPRINT_LINE, 1);
    gfx.strokeRect(px, py, size, size);
    this.blueprintGraphics = gfx;
  }

  remove() {
    this.sprite?.destroy();
    this.sprite = undefined;
    this.outlineGraphics?.destroy();
    this.outlineGraphics = undefined;
    this.materialOutlineGraphics?.destroy();
    this.materialOutlineGraphics = undefined;
    this.blueprintGraphics?.destroy();
    this.blueprintGraphics = undefined;
    this.hideLockTimer();
  }
}
